import React, { useEffect, useState } from 'react';
import { brigadeiros, bombons } from '../data/mockData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Plus, Minus } from 'lucide-react';

const FlavorSelector = ({ isOpen, onClose, onAddToCart, boxInfo, productType }) => {
  const [selectedFlavors, setSelectedFlavors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setSelectedFlavors({});
    }
  }, [isOpen]);
  
  const flavorList = productType === 'brigadeiros' 
    ? brigadeiros 
    : bombons;

  const maxFlavors = boxInfo?.flavors || 2;
  const totalUnits = boxInfo?.units || 0;
  const totalSelected = Object.values(selectedFlavors).reduce((sum, count) => sum + count, 0);
  const selectedFlavorTypes = Object.keys(selectedFlavors).length;

  const handleFlavorAdd = (flavorName) => {
    const isNewFlavor = !selectedFlavors[flavorName];
    const canAddNewFlavor = !isNewFlavor || selectedFlavorTypes < maxFlavors;

    if (totalSelected < totalUnits && canAddNewFlavor) {
      setSelectedFlavors(prev => ({
        ...prev,
        [flavorName]: (prev[flavorName] || 0) + 1
      }));
    }
  };

  const handleFlavorRemove = (flavorName) => {
    setSelectedFlavors(prev => {
      const newCount = (prev[flavorName] || 1) - 1;
      if (newCount <= 0) {
        const { [flavorName]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [flavorName]: newCount };
    });
  };

  const handleAddToCart = () => {
    if (totalSelected === totalUnits) {
      const flavorsList = [];
      Object.entries(selectedFlavors).forEach(([flavor, count]) => {
        for (let i = 0; i < count; i++) {
          flavorsList.push(flavor);
        }
      });

      onAddToCart({
        productType: productType === 'brigadeiros' ? 'Brigadeiros' : 'Bombons',
        quantity: boxInfo.units,
        flavors: flavorsList,
        price: boxInfo.price
      });
      setSelectedFlavors({});
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-amber-950">
            Escolha até {maxFlavors} sabores e distribua {totalUnits} unidades
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-3 py-4">
          {flavorList.map((flavor) => {
            const count = selectedFlavors[flavor.name] || 0;
            return (
              <div key={flavor.id} className="flex items-center justify-between bg-amber-50 p-3 rounded-lg border border-amber-200">
                <Label 
                  className="text-sm text-amber-900 cursor-pointer flex-1"
                >
                  {flavor.name}
                </Label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFlavorRemove(flavor.name)}
                    disabled={count === 0}
                    className="h-8 w-8 p-0"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="w-8 text-center font-semibold text-amber-950">
                    {count}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleFlavorAdd(flavor.name)}
                    disabled={
                      totalSelected >= totalUnits ||
                      (!selectedFlavors[flavor.name] && selectedFlavorTypes >= maxFlavors)
                    }
                    className="h-8 w-8 p-0"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-amber-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Unidades selecionadas: {totalSelected}/{totalUnits}</strong>
          </p>
          <p className="text-sm text-amber-800 mt-1">
            <strong>Sabores diferentes: {selectedFlavorTypes}/{maxFlavors}</strong>
          </p>
          {totalSelected > 0 && (
            <p className="text-xs text-amber-700 mt-2">
              {Object.entries(selectedFlavors).map(([flavor, count]) => 
                `${flavor} (${count})`
              ).join(', ')}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onClose}
            className="border-amber-200 text-amber-800"
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleAddToCart}
            disabled={totalSelected !== totalUnits}
            className="bg-amber-700 hover:bg-amber-800 text-white"
          >
            Adicionar ao Carrinho
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FlavorSelector;
